import { useId, useRef, useState } from "react";
import { useAuth } from "../auth/AuthContext";
import useDialog from "../hooks/useDialog";
import useClassProgress from "../data/useClassProgress";
import { OFFLINE, problemOf } from "../data/problems";
import { announceWorkspaceChange } from "../data/usePolledResource";
import { BackIcon, CloseIcon } from "./Icons";
import LearnerWork from "./LearnerWork";
import { STATE, StatePill, StepProgress } from "./WorkState";
import ui from "./ui.module.css";

const ORDER = ["working", "completed", "reviewed", "not_started"];

const Summary = ({ learners }) => {
  const counts = learners.reduce((all, { state }) => ({ ...all, [state]: (all[state] || 0) + 1 }), {});
  const parts = ORDER.filter((state) => counts[state]).map((state) => `${counts[state]} ${STATE[state].toLowerCase()}`);
  return <p className={ui.progressSummary}>{parts.join(" · ")}</p>;
};

// Refused because this app's sign-in has ended: retrying can't help.
const needsSignIn = (error) =>
  problemOf(error) !== OFFLINE && error?.response?.status === 401;

const ClassObserveDialog = ({ classroomId, deliverySyncKey, activityId, onClose }) => {
  const { signOut } = useAuth();
  const titleId = useId();
  const closeRef = useRef(null);
  useDialog(onClose, closeRef);
  const progress = useClassProgress({ classroomId, deliverySyncKey, activityId });
  const [selected, setSelected] = useState(null);
  const learners = progress.data?.learners || [];
  return (
    <div className={ui.sheetBackdrop} role="presentation">
      <section className={`${ui.sheet} ${ui.observeSheet}`} role="dialog" aria-modal="true" aria-labelledby={titleId}>
        <div className={ui.row}>
          <div>
            <h2 id={titleId}>Class progress</h2>
            {progress.data?.activity?.title && <p className={ui.meta}>{progress.data.activity.title}</p>}
          </div>
          <button ref={closeRef} type="button" className={ui.iconButton} onClick={onClose} aria-label="Close class progress">
            <CloseIcon />
          </button>
        </div>
        {progress.status === "loading" && <p role="status">Loading class progress…</p>}
        {progress.status === "error" && !progress.data && (needsSignIn(progress.error) ? (
          <p className={ui.error} role="alert">
            Your sign-in has ended. Sign in again to see class progress.{" "}
            <button type="button" className={ui.textButton} onClick={signOut}>Sign in again</button>
          </p>
        ) : (
          <p className={ui.error} role="alert">
            Class progress could not load. It will try again in a moment.{" "}
            <button type="button" className={ui.textButton} onClick={progress.refresh}>Try again now</button>
          </p>
        ))}
        {progress.status === "error" && progress.data && !selected && (
          <p className={ui.meta} role="status">
            Can't refresh right now. Showing the last update.{" "}
            <button type="button" className={ui.textButton} onClick={progress.refresh}>Try again</button>
          </p>
        )}
        {progress.data && !selected && (learners.length ? (
          <>
            <Summary learners={learners} />
            <ul className={ui.learnerGrid}>
              {learners.map((learner) => (
                <li key={learner.id}>
                  <button
                    type="button"
                    className={`${ui.learnerCard} ${ui[`state_${learner.state}`] || ""}`}
                    disabled={!learner.attemptId}
                    onClick={() => setSelected(learner.attemptId)}
                  >
                    <span className={ui.learnerCardTop}>
                      <span className={ui.learnerName}>{learner.name}</span>
                      <StatePill state={learner.state} />
                    </span>
                    {/* State and steps only: answers belong in the learner's work. */}
                    <StepProgress completedSteps={learner.completedSteps} totalSteps={learner.totalSteps} />
                  </button>
                </li>
              ))}
            </ul>
          </>
        ) : <p>No learners are enrolled in this class yet.</p>)}
        {selected && (
          <>
            <button type="button" className={ui.textButton} onClick={() => setSelected(null)}><BackIcon size={16} />Back to class</button>
            <LearnerWork
              attemptId={selected}
              onSaved={() => { progress.refresh(); announceWorkspaceChange(); }}
              onDone={() => setSelected(null)}
            />
          </>
        )}
      </section>
    </div>
  );
};

export default ClassObserveDialog;
